import dayjs from 'dayjs';

import request from '../utils/Request';
import { Methods, NotificationType } from '../config';
import { downloadFile } from '../utils/download';
import addNotification from '../utils/addNotification';
import { ResponseType } from '../config/responetype';

const baseURL = 'item';

const itemService = {
  getItems: (data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/items`,
      data,
    });
  },

  getItemById: (itemCode) => {
    return request({
      method: Methods.GET,
      resource: `${baseURL}/item/${encodeURIComponent(itemCode)}`,
    });
  },

  getHistoryById: (itemCode) => {
    return request({
      method: Methods.GET,
      resource: `${baseURL}/history/${encodeURIComponent(itemCode)}`,
    });
  },

  updateItem: (data, itemCode) => {
    return request({
      method: Methods.PUT,
      resource: `${baseURL}/${encodeURIComponent(itemCode)}`,
      formData: true,
      data,
    });
  },

  createItem: (data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}`,
      formData: true,
      data,
    });
  },

  deleteItem: (data, itemCode) => {
    return request({
      method: Methods.DELETE,
      resource: `${baseURL}/${encodeURIComponent(itemCode)}`,
      data,
    });
  },

  getExcelTemplate: async () => {
    const excelFromServer = await request({
      responseType: 'blob',
      resource: `${baseURL}/exceltemplate`,
    });
    return downloadFile(excelFromServer, `cikkek_template-${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
  },

  getExcelData: async () => {
    const excelFromServer = await request({
      responseType: 'blob',
      resource: `${baseURL}/exceldata`,
    });
    return downloadFile(excelFromServer, `cikkek-${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
  },

  importExcel: async (files, userId) => {
    const formData = new FormData();

    for (const file of files) {
      formData.append('files', file);
    }
    formData.append('userId', userId);

    const resp = await request({
      method: Methods.POST,
      resource: `${baseURL}/excelimport`,
      formData: true,
      data: formData,
      responseType: 'blob',
    });

    if (resp?.type === ResponseType.xlsx) {
      addNotification({
        content: 'Az import sikertelen!',
        type: NotificationType.ERROR,
      });

      downloadFile(resp, `ERR_${files[0].name}`);
      return Promise.reject(new Error('Error in uploaded file!'));
    }

    const reader = new FileReader();
    reader.readAsText(resp);
    reader.onload = () => {
      const jsonData = JSON.parse(reader.result);
      if (jsonData?.error?.message) {
        return addNotification({
          content: jsonData.error.message,
          type: NotificationType.ERROR,
        });
      }
      return jsonData.data;
    };
  },

  getPrices: (itemCode, priceListId) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/prices`,
      data: { itemCode, priceListId },
    });
  },

  savePrices: (data) => {
    return request({
      method: Methods.PUT,
      resource: `${baseURL}/prices`,
      data,
    });
  },

  findItem: (data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/findItem`,
      data,
    });
  },

  findItemFromStock: (data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/findItemFromStock`,
      data,
    });
  },

  createLabel: (data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/createLabel`,
      data,
      responseType: 'blob',
    });
  },

  printDocument: async (data) => {
    const pdf = await request({
      responseType: 'blob',
      method: Methods.POST,
      resource: `${baseURL}/print`,
      data,
    });

    return downloadFile(pdf, pdf.filename);
  },

  getItemStock: (itemCode) => {
    return request({
      method: Methods.GET,
      resource: `${baseURL}/stock/${encodeURIComponent(itemCode)}`,
    });
  },

  getCalculatedPrice: (itemCode) => {
    return request({
      method: Methods.GET,
      resource: `${baseURL}/calculatedPrice/${encodeURIComponent(itemCode)}`,
    });
  },

  generateItemCode: (itemGroupId) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/generateItemCode`,
      data: itemGroupId ? { itemGroupId } : {},
    });
  },

  getItemCodePolicy: (data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/itemCode/policy`,
      data,
    });
  },

  generateItemCodeByStrategy: (data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/itemCode/generate`,
      data,
    });
  },

  generateBatch: (itemCode) => {
    return request({
      method: Methods.GET,
      resource: `${baseURL}/generateBatch/${encodeURIComponent(itemCode)}`,
    });
  },
};
export default itemService;
